import { useNavigate } from "react-router-dom";
import { SEO } from "@/components/SEO";
import {
  GraduationCap, Home, Building2, Users, ArrowRight, BookOpen, Award,
  Clock, CheckCircle, Star, ArrowLeft, ArrowUpRight, Heart, ChevronRight,
} from "lucide-react";
import { useAuth } from "../App";
import { C, DISPLAY, BODY, MONO, Reveal, EditorialLabel, EBtn } from "@/styles/editorial";

const TRACKS = [
  {
    id: "familias", title: "Familias", subtitle: "Tu plan, tus animales",
    desc: "Aprende a proteger a tus animales de compañía antes, durante y después de una emergencia. Curso completo para familias.",
    icon: Home, c: C.orange, lessons: 8, duration: "4-6h", level: "Básico", available: true,
    topics: ["Kit de emergencia", "Planes de evacuación", "Peligros meteorológicos", "Recuperación post-desastre"],
  },
  {
    id: "protectoras", title: "Protectoras", subtitle: "Gestión de emergencias en refugios",
    desc: "Formación especializada para refugios y protectoras. Gestiona emergencias con múltiples animales.",
    icon: Building2, c: C.blue, lessons: 10, duration: "8-10h", level: "Intermedio", available: false,
    topics: ["Evacuación masiva", "Gestión de recursos", "Coordinación con autoridades", "Primeros auxilios veterinarios"],
  },
  {
    id: "voluntarios", title: "Voluntarios", subtitle: "Formación para ayudar en emergencias",
    desc: "Conviértete en voluntario certificado. Aprende técnicas de rescate y manejo de animales.",
    icon: Users, c: C.pink, lessons: 12, duration: "10-12h", level: "Avanzado", available: false,
    topics: ["Técnicas de rescate", "Manejo de animales estresados", "Coordinación con equipos", "Primeros auxilios avanzados"],
  },
];

export default function AprendeLanding() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen" style={{ background: C.white, fontFamily: BODY }}>
      <SEO
        title="Aprende"
        description="Cursos gratuitos sobre protección animal en emergencias. Formación para familias, protectoras y emergencias. Incluye sección infantil con cuentos interactivos."
        path="/aprende"
      />
      {/* Header */}
      <header className="sticky top-0 z-50 px-6 md:px-12 py-4"
        style={{ background: "rgba(255,255,255,0.92)", backdropFilter: "blur(12px)", borderBottom: `1px solid ${C.navy}15` }}>
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm font-semibold"
              style={{ fontFamily: BODY, color: C.blue, background: "none", border: "none", cursor: "pointer" }}>
              <ArrowLeft className="w-4 h-4" /> Hub
            </button>
            <div className="h-5 w-px" style={{ background: `${C.navy}20` }} />
            <div>
              <span className="text-sm font-bold tracking-tight" style={{ fontFamily: DISPLAY, color: C.navy }}>Aprende</span>
              <p className="text-[9px] tracking-[0.2em] font-medium uppercase" style={{ fontFamily: MONO, color: C.lilac }}>Emergencia Animal</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/aprende/kids")}
              className="hidden md:flex items-center gap-1.5 px-4 py-2 text-sm font-medium"
              style={{ fontFamily: BODY, color: C.pink, background: "none", border: "none", cursor: "pointer" }}>
              Kids <Star className="w-3.5 h-3.5" />
            </button>
            <EBtn onClick={() => { if (user) navigate("/aprende/panel"); else navigate("/auth"); }} bg={C.navy} color={C.white} testId="aprende-header-btn">
              {user ? "Mi panel" : "Acceder"} <ArrowUpRight className="w-3.5 h-3.5" />
            </EBtn>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 md:px-12 pt-16 pb-20 md:pt-24 md:pb-28">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-8">
            <Reveal>
              <EditorialLabel text="Formación en emergencias" color={C.lilac} />
              <h1 className="text-[clamp(2.5rem,6vw,4.5rem)] leading-[0.95] tracking-tight mb-6" style={{ fontFamily: DISPLAY, color: C.navy }}>
                El conocimiento<br />
                <span style={{ color: C.lilac }}>salva vidas</span>
              </h1>
              <p className="text-base md:text-lg max-w-xl mb-4 leading-relaxed" style={{ color: C.gray }}>
                Cursos de formación basados en protocolos FEMA. Quizzes interactivos, certificados y badges de logros.
                <strong style={{ color: C.navy }}> Prepárate para proteger a los animales.</strong>
              </p>
              <p className="text-xs mb-8" style={{ fontFamily: MONO, color: C.lilac }}>3 tracks · Certificados · Basado en FEMA</p>
              <div className="flex flex-wrap gap-4">
                <EBtn onClick={() => { if (user) navigate("/aprende/panel"); else navigate("/auth"); }} bg={C.lilac} color={C.navy} testId="aprende-hero-start-btn">
                  Empezar a aprender <ArrowRight className="w-4 h-4" />
                </EBtn>
                <EBtn onClick={() => navigate("/aprende/kids")} outline bg={C.pink}>
                  Zona Kids <Star className="w-4 h-4" />
                </EBtn>
              </div>
            </Reveal>
          </div>

          {/* Features sidebar */}
          <div className="md:col-span-4 space-y-4">
            {[
              { icon: GraduationCap, t: "Cursos certificados", c: C.lilac },
              { icon: BookOpen, t: "Contenido basado en FEMA", c: C.blue },
              { icon: Award, t: "Certificados PDF descargables", c: C.orange },
              { icon: CheckCircle, t: "Quizzes interactivos", c: C.olive },
            ].map((f, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="flex items-center gap-3 py-3" style={{ borderBottom: `1px solid ${C.navy}08` }}>
                  <f.icon className="w-5 h-5 flex-shrink-0" style={{ color: f.c }} strokeWidth={1.5} />
                  <span className="text-sm" style={{ color: C.navy }}>{f.t}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Tracks */}
      <section className="px-6 md:px-12 py-16 md:py-20" style={{ background: C.bg }}>
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <EditorialLabel number="01" text="Tracks formativos" color={C.lilac} />
            <h2 className="text-[clamp(1.8rem,4vw,2.8rem)] leading-[1.05] tracking-tight mb-12" style={{ fontFamily: DISPLAY, color: C.navy }}>
              Tres caminos, <span style={{ color: C.lilac }}>un objetivo</span>
            </h2>
          </Reveal>

          <div className="space-y-6">
            {TRACKS.map((track, idx) => {
              const TIcon = track.icon;
              return (
                <Reveal key={track.id} delay={idx * 0.08}>
                  <div
                    className="grid md:grid-cols-12 gap-6 p-7 md:p-8 transition-all cursor-pointer"
                    style={{
                      background: C.white, borderRadius: 6,
                      border: `1px solid ${C.navy}10`,
                      borderLeft: `4px solid ${track.c}`,
                      opacity: track.available ? 1 : 0.7,
                    }}
                    onClick={() => {
                      if (track.available) {
                        if (user) navigate(`/aprende/${track.id}`);
                        else navigate("/auth");
                      }
                    }}
                    data-testid={`track-card-${track.id}`}
                  >
                    <div className="md:col-span-4">
                      <div className="flex items-center gap-3 mb-3">
                        <TIcon className="w-6 h-6" style={{ color: track.c }} strokeWidth={1.5} />
                        <div>
                          <h3 className="text-xl tracking-tight" style={{ fontFamily: DISPLAY, color: C.navy }}>{track.title}</h3>
                          <p className="text-xs" style={{ fontFamily: MONO, color: track.c }}>{track.subtitle}</p>
                        </div>
                      </div>
                      <div className="flex gap-3 mt-3">
                        <span className="flex items-center gap-1 text-xs" style={{ fontFamily: MONO, color: C.lightgray }}>
                          <BookOpen className="w-3 h-3" /> {track.lessons} lecciones
                        </span>
                        <span className="flex items-center gap-1 text-xs" style={{ fontFamily: MONO, color: C.lightgray }}>
                          <Clock className="w-3 h-3" /> {track.duration}
                        </span>
                      </div>
                    </div>

                    <div className="md:col-span-4">
                      <p className="text-sm leading-relaxed" style={{ color: C.gray }}>{track.desc}</p>
                    </div>

                    <div className="md:col-span-4">
                      <p className="text-[10px] font-semibold tracking-wider uppercase mb-2" style={{ fontFamily: MONO, color: C.lightgray }}>Temas</p>
                      <ul className="space-y-1">
                        {track.topics.map((t, i) => (
                          <li key={i} className="flex items-center gap-1.5 text-xs" style={{ color: C.gray }}>
                            <ChevronRight className="w-3 h-3" style={{ color: track.c }} strokeWidth={2} /> {t}
                          </li>
                        ))}
                      </ul>
                      {!track.available && (
                        <span className="inline-block mt-3 px-2.5 py-1 text-[9px] font-bold tracking-wider uppercase"
                          style={{ fontFamily: MONO, background: `${C.navy}08`, color: C.lightgray, borderRadius: 3 }}>
                          Próximamente
                        </span>
                      )}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Kids CTA */}
      <section className="px-6 md:px-12 py-16 md:py-20">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <Reveal>
              <EditorialLabel number="02" text="Para los más pequeños" color={C.pink} />
              <h2 className="text-[clamp(1.8rem,4vw,2.8rem)] leading-[1.05] tracking-tight mb-4" style={{ fontFamily: DISPLAY, color: C.navy }}>
                Zona <span style={{ color: C.pink }}>Kids</span>
              </h2>
              <p className="text-sm leading-relaxed max-w-md mb-6" style={{ color: C.gray }}>
                Cuentos, juegos y actividades para que los más pequeños aprendan a proteger a los animales.
                Para héroes y heroínas de 3 a 14 años.
              </p>
              <EBtn onClick={() => navigate("/aprende/kids")} bg={C.pink} color={C.white}>
                Ir a zona Kids <Star className="w-4 h-4" />
              </EBtn>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.15}>
              <div className="p-8 text-center" style={{ background: `${C.pink}10`, borderRadius: 6 }}>
                <div className="flex justify-center gap-4 mb-4">
                  {[BookOpen, Award, Heart, Star].map((I, i) => (
                    <I key={i} className="w-8 h-8" style={{ color: C.pink }} strokeWidth={1.2} />
                  ))}
                </div>
                <p className="text-sm" style={{ fontFamily: MONO, color: C.pink }}>Cuentos · Juegos · Actividades</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28" style={{ background: C.navy }}>
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Reveal>
            <GraduationCap className="w-8 h-8 mb-5" style={{ color: C.lilac }} strokeWidth={1.5} />
            <h2 className="text-[clamp(2rem,5vw,3.5rem)] leading-[1.05] tracking-tight mb-4" style={{ fontFamily: DISPLAY, color: C.white }}>
              Aprende hoy.<br />
              <span style={{ color: C.orange }}>Salva mañana.</span>
            </h2>
            <p className="text-base mb-8 max-w-lg leading-relaxed" style={{ color: `${C.white}80` }}>
              Cada persona formada es una vida protegida. Empieza tu camino de aprendizaje.
            </p>
            <EBtn onClick={() => { if (user) navigate("/aprende/panel"); else navigate("/auth"); }} bg={C.lilac} color={C.navy}>
              Empezar ahora <ArrowRight className="w-4 h-4" />
            </EBtn>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
